import { Children, ReactElement, useRef, useState } from 'react';
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Form,
  FormInstance,
  FormItemProps,
  Modal,
  ModalProps,
  Radio,
  Select,
  Space,
  Table,
  TableProps,
} from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { ColumnsType, ColumnType } from 'antd/es/table';

import { SelectTable } from '../../select-table';

export type DataTableProps<RecordType> = Omit<TableProps<RecordType>, 'onChange'> & {
  children: ReactElement<FormItemProps> | ReactElement<FormItemProps>[];
  form?: FormInstance<RecordType>;
  modalProps?: Omit<ModalProps, 'open'>;
  recordType?: string;
  value?: RecordType[];
  onChange?: (newValue: RecordType[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onValuesChange?: (changedValues: any) => void;
};

export const DataTable = <RecordType extends { id: number }>({
  children,
  form: formFromProps,
  modalProps,
  recordType,
  value = [],
  onChange,
  onValuesChange,
  ...tableProps
}: DataTableProps<RecordType>) => {
  const [_form] = Form.useForm<RecordType>();
  const [open, setOpen] = useState<boolean | RecordType>(false);
  const currentKey = useRef(value.length);

  const form = formFromProps ?? _form;

  const onCancel = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (e) {
      modalProps?.onCancel && modalProps.onCancel(e);
    }

    form.resetFields();
    setOpen(false);
  };

  const onDelete = (record: RecordType) => {
    const newValue = value.filter((item) => item !== record);
    onChange && onChange(newValue);
  };

  const onEdit = (record: RecordType) => {
    Object.entries(record).forEach(([key, value]) => form.setFieldValue(key, value));
    setOpen(record);
  };

  const onFinish = (data: RecordType) => {
    if (typeof open === 'boolean') {
      onChange && onChange(value.concat({ ...data, id: -++currentKey.current }));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.entries(data).forEach(([key, value]) => (open[key as keyof RecordType] = value as any));
    }

    onCancel();
  };

  const onOk = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    modalProps?.onOk && modalProps.onOk(e);
    form.submit();
  };

  const columns: ColumnsType<RecordType> = [
    {
      width: '100px',
      title: 'Action',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => onEdit(record)}>
            Edit
          </Button>
          <Button icon={<DeleteOutlined />} size="small" onClick={() => onDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
    ...Children.map(children, (child: ReactElement<FormItemProps>) => {
      const grandchild = Children.only(child.props.children as ReactElement);

      const column: ColumnType<RecordType> = {
        dataIndex: [child.props.name],
        title: child.props.label,
      };

      if (grandchild.type === Checkbox) {
        column.render = (value: boolean) => (value ? <CheckOutlined /> : '');
      } else if (grandchild.type === Select) {
        if (grandchild.props.mode === 'multiple') {
          column.render = (value: string[] | DefaultOptionType[]) =>
            value
              .map((x) => (grandchild.props.labelInValue ? (x as DefaultOptionType).label : (x as string)))
              .join('; ');
        } else if (grandchild.props.labelInValue) {
          (column.dataIndex as string[])?.push('label');
        }
      } else if (grandchild.type === Radio.Group) {
        column.render = (value: string | number) => {
          const target = grandchild.props.options.find((x: string | number | DefaultOptionType) => {
            if (typeof x === 'object') return x.value === value;
            return x === value;
          });

          return typeof target === 'object' ? target.label : target;
        };
      } else if (grandchild.type === DataTable || grandchild.type === SelectTable) {
        return null;
      }

      return column;
    }),
  ];

  return (
    <>
      <Modal
        maskClosable={false}
        open={!!open}
        onCancel={onCancel}
        onOk={onOk}
        title={`${typeof open === 'boolean' ? 'Add' : 'Edit'} ${recordType ?? ''}`.trim()}
        {...modalProps}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={onValuesChange}>
          {children}
        </Form>
      </Modal>
      <Button icon={<PlusOutlined />} style={{ marginBottom: '0.5em' }} onClick={() => setOpen(true)}>
        {`Add ${recordType ?? ''}`.trim()}
      </Button>
      <Table rowKey="id" size="small" columns={columns.filter((x) => x)} dataSource={value} {...tableProps} />
    </>
  );
};
